import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Background } from "../components/Background";
import { Countdown } from "../components/Countdown";
import { GameCanvas } from "../components/GameCanvas";
import { Button } from "../components/ui/Button";
import { randomSeed } from "../game/engine/prng";

const COUNTDOWN_SEC = 3;

export default function SinglePlayerGame() {
  const navigate = useNavigate();
  const name = (localStorage.getItem("cap.name") || "Cap").slice(0, 20);
  const [seed, setSeed] = useState(() => randomSeed());
  const [phase, setPhase] = useState<"countdown" | "playing" | "over">("countdown");
  const [score, setScore] = useState(0);

  const onGameOver = useCallback((final: number) => {
    setScore(final);
    setPhase("over");
  }, []);

  const handleRestart = () => {
    setScore(0);
    setSeed(randomSeed());
    setPhase("countdown");
  };

  const levelConfig = useMemo(() => ({ seed }), [seed]);

  return (
    <Background>
      <div className="mx-auto flex min-h-screen max-w-[1400px] flex-col gap-4 p-3 sm:p-6">
        <TopBar
          title="Solo Run"
          onExit={() => navigate("/")}
        />
        <div className="relative flex-1">
          <GameCanvas
            levelConfig={levelConfig}
            selfName={name}
            onGameOver={onGameOver}
            startDelayMs={COUNTDOWN_SEC * 1000}
          />
          {phase === "countdown" && (
            <Countdown
              seconds={COUNTDOWN_SEC}
              onDone={() => setPhase("playing")}
            />
          )}
          {phase === "over" && (
            <GameOverOverlay
              score={score}
              onRestart={handleRestart}
              onExit={() => navigate("/")}
            />
          )}
        </div>
      </div>
    </Background>
  );
}

function TopBar({ title, onExit }: { title: string; onExit: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <div className="font-display text-xl uppercase tracking-[0.3em] text-white/80">
        {title}
      </div>
      <Button variant="ghost" size="sm" onClick={onExit}>
        Exit
      </Button>
    </div>
  );
}

function GameOverOverlay({
  score,
  onRestart,
  onExit,
}: {
  score: number;
  onRestart: () => void;
  onExit: () => void;
}) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-cap-deep/85 backdrop-blur-sm">
      <div className="rounded-2xl border border-white/15 bg-cap-deep/80 p-8 text-center shadow-hero">
        <div className="font-display text-xs uppercase tracking-[0.4em] text-cap-red">
          Mission Failed
        </div>
        <div className="mt-2 font-display text-7xl text-cap-gold drop-shadow-[0_0_20px_rgba(240,179,35,0.4)]">
          {score.toLocaleString()}
        </div>
        <div className="mt-1 text-xs uppercase tracking-widest text-white/60">Final Score</div>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button variant="primary" size="lg" onClick={onRestart}>
            Run Again
          </Button>
          <Button variant="ghost" size="lg" onClick={onExit}>
            Home
          </Button>
        </div>
      </div>
    </div>
  );
}
